/**
 * Password rules - the client-side mirror of
 * backend/auth_utils.py::validate_password_strength.
 *
 * WHY THIS FILE EXISTS
 * The rules were written out twice, once in LoginPage and once in
 * ResetPasswordPage, each with its own regexes. Adding the special-character
 * requirement to one and not the other would show a user green ticks on every
 * line and then reject the password on submit, with no explanation of which
 * rule they broke. One list, used by both screens.
 *
 * KEEP IN SYNC WITH THE BACKEND. The backend is the authority - it is what
 * actually rejects a weak password. This list only decides what the user sees
 * while typing, so if the two disagree the user is misled, not protected.
 */

// THE SAME LIST THE SERVER USES, CHARACTER FOR CHARACTER.
// (30 September 2026.)
//
// This was /[^A-Za-z0-9\s]/ - "anything that is not a letter, digit or
// whitespace" - which is a DIFFERENT RULE from the backend's fixed list, and
// a kinder one. So a password ending in £ or € or a curly quote showed five
// green ticks here and was refused on submit with "must contain at least one
// special character", the very rule the screen had just ticked.
//
// Not hypothetical: an iPhone turns a typed apostrophe into ’ and a double
// hyphen into –, and neither is in the server's list. The user would have no
// way to tell what was wrong, because the screen said nothing was.
//
// A STRING, NOT A CHARACTER CLASS. The backend tests membership in a literal
// string; writing the same set as a regex means escaping ] \ ^ and - and
// getting one of them wrong reintroduces the difference silently. Membership
// is what the backend does, so membership is what this does.
//
// Must stay identical to PASSWORD_SPECIALS in backend/auth_utils.py.
// backend/tests/test_password_rules_parity.py feeds generated passwords to
// both and requires the same verdict.
export const PASSWORD_SPECIALS = "!@#$%^&*()-_=+[]{}|;:'\",.<>/?`~\\";

export const isSpecialChar = (ch) => PASSWORD_SPECIALS.includes(ch);

const hasSpecial = (pw) => [...pw].some(isSpecialChar);

export const MIN_PASSWORD_LENGTH = 8;

/** [{ key, ok }] for every rule, in display order. */
export function passwordChecks(password) {
  const pw = password || "";
  return [
    { key: "len", ok: pw.length >= MIN_PASSWORD_LENGTH },
    { key: "up", ok: /[A-Z]/.test(pw) },
    { key: "low", ok: /[a-z]/.test(pw) },
    { key: "num", ok: /[0-9]/.test(pw) },
    { key: "special", ok: hasSpecial(pw) },
  ];
}

/** True when the password satisfies every rule. */
export function isPasswordValid(password) {
  return passwordChecks(password).every((c) => c.ok);
}

/** Keys of the rules not yet satisfied - empty means valid. */
export function passwordFailures(password) {
  return passwordChecks(password).filter((c) => !c.ok).map((c) => c.key);
}
